import React, { memo, useEffect, useCallback, FC } from 'react'

import { useMutation } from '@apollo/client'
import { useForm } from 'react-hook-form'

import { EMAIL_PATTERN, SIGN_IN_CUSTOMER } from 'Const'
import { User } from 'Types'

import ErrorMessage from 'Components/ErrorMessage'

import FormErrors from '../../FormErrors'

interface Props {
  closeModal(): void
  openForgotPasswordModal(): void
}

const LoginModal: FC<Props> = ({ closeModal, openForgotPasswordModal }) => {
  const { register, handleSubmit, setError, errors } = useForm()

  const [signCustomer, { error }] = useMutation(SIGN_IN_CUSTOMER, {
    update(cache, { data }) {
      cache.modify({
        fields: {
          currentUser() {
            return {
              ...(data.signInCustomer.user as User),
              __typename: 'User',
            }
          },
        },
      })
    },
  })

  useEffect(() => {
    if (error) {
      error.graphQLErrors.forEach((error) => {
        if (error.extensions?.fieldName) {
          setError(error.extensions.fieldName, { message: error.message })
        }
      })
    }
  }, [error, setError])

  const onSubmit = useCallback(
    async (payload) => {
      void (await signCustomer({ variables: payload }).then(() => closeModal()))
    },
    [closeModal, signCustomer],
  )

  const handleForgotPasswordClick = useCallback(() => {
    closeModal()
    openForgotPasswordModal()
  }, [closeModal, openForgotPasswordModal])

  return (
    <>
      <div className="modal fade show" style={{ display: 'block' }}>
        <div className="modal-dialog modal-dialog-centered">
          <form className="modal-content" onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-header align-items-center">
              <h4 className="modal-title">Sign In</h4>
              <button aria-hidden="true" className="close pull-right" type="button" onClick={closeModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group row align-items-center">
                <label className="col-sm-2 mb-0" htmlFor="customer-email">
                  Email
                </label>
                <div className="col-sm-10">
                  <input
                    ref={register({
                      required: 'Email is required',
                      pattern: {
                        value: EMAIL_PATTERN,
                        message: 'Email incorrect',
                      },
                    })}
                    required
                    className="form-control mb-0"
                    id="customer-email"
                    name="email"
                    placeholder="Enter your email..."
                    type="email"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="form-group row align-items-center">
                <label className="col-sm-2 mb-0" htmlFor="customer-email">
                  Password
                </label>
                <div className="col-sm-10">
                  <input
                    ref={register({
                      required: 'Password is required',
                      minLength: {
                        message: 'Password min length is 6',
                        value: 6,
                      },
                    })}
                    className="form-control"
                    id="customer-password"
                    name="password"
                    placeholder="Password"
                    type="password"
                    autoComplete="current-password"
                  />
                </div>
              </div>

              <div className="d-flex align-items-center">
                <div className="mb-0">
                  <ErrorMessage as={FormErrors} errors={errors} name="password" />
                </div>
              </div>

              <div className="form-group row align-items-center">
                <div className="col-sm-2"></div>
                <div className="col-sm-10">
                  <a className="text-underline" onClick={handleForgotPasswordClick}>
                    Forgot your password?
                  </a>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-accent" type="submit">
                Sign in
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  )
}

export default memo(LoginModal)
